import React, { useEffect, useRef } from "react";
import {
  Container,
  Header,
  Icon,
  Link,
  SpaceBetween,
  Tabs,
  TabsProps,
} from "@amzn/awsui-components-react";

type Stage = "settings" | "review" | "result";

export default function OMSWorkflow({
  children,
  footer,
  heading,
  headingInfo,
  focusHeading = false,
  stage,
  completionLabel = "Export",
  parent,
}: {
  children?: React.ReactNode;
  footer?: React.ReactNode;
  heading?: string;
  headingInfo?: React.ReactNode;
  focusHeading?: boolean;
  stage?: Stage;
  completionLabel?: "Export" | "Saved";
  parent?: { text: string; href: string; onFollow: () => void };
}) {
  const stageLabels = {
    settings: "Settings",
    review: "Review",
    result: completionLabel,
  };
  const headingRoot = useRef<HTMLDivElement>(null);
  const wasFocused = useRef(false);
  useEffect(() => {
    if (focusHeading || wasFocused.current) {
      const title = headingRoot.current?.querySelector("h2");
      title?.setAttribute("tabindex", "-1");
      title?.focus();
    }
    wasFocused.current = focusHeading;
  }, [focusHeading, heading]);
  return (
    <section
      className="oms-page-workflow"
      aria-label={heading || "Export workspace"}
    >
      <SpaceBetween size="l">
        {parent && heading && (
          <nav
            className="oms-workflow-breadcrumb"
            aria-label="Preset navigation"
          >
            <ol>
              <li>
                <Link
                  href={parent.href}
                  fontSize="inherit"
                  onFollow={(event) => {
                    event.preventDefault();
                    parent.onFollow();
                  }}
                >
                  {parent.text}
                </Link>
              </li>
              <li>
                <span aria-hidden="true">
                  <Icon name="angle-right" />
                </span>
                <span aria-current="page">{heading}</span>
              </li>
            </ol>
          </nav>
        )}
        {heading && (
          <div className="oms-workflow-heading">
            <div ref={headingRoot}>
              <Header variant="h2" info={headingInfo}>
                {heading}
              </Header>
            </div>
            {stage && (
              <ol
                className="oms-workflow-stages"
                aria-label="Workflow progress"
              >
                {(["settings", "review", "result"] as Stage[]).map((item) => (
                  <li
                    key={item}
                    aria-current={item === stage ? "step" : undefined}
                  >
                    {item !== "settings" && (
                      <span aria-hidden="true">
                        <Icon name="angle-right" />
                      </span>
                    )}
                    <span>{stageLabels[item]}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
        {children}
      </SpaceBetween>
      {footer && <div className="oms-page-workflow__footer">{footer}</div>}
    </section>
  );
}

export function OMSWorkflowSection({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Container
      className="oms-workflow-section"
      header={<Header variant="h3">{title}</Header>}
    >
      <SpaceBetween size="m">{children}</SpaceBetween>
    </Container>
  );
}

export function OMSSettingsSection({
  title,
  notice,
  directSingle = true,
  ...tabs
}: TabsProps & {
  title: string;
  notice?: React.ReactNode;
  directSingle?: boolean;
}) {
  return (
    <OMSWorkflowSection title={title}>
      {notice}
      {directSingle && tabs.tabs.length === 1 ? (
        tabs.tabs[0].content
      ) : (
        <Tabs {...tabs} />
      )}
    </OMSWorkflowSection>
  );
}
